import React, { useState, useEffect, useCallback } from 'react';
import { Cookie, X, Shield, Settings } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Use a module-level flag to prevent re-showing during the same session
let hasBeenDismissedThisSession = false;

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // If already dismissed this session, don't show again
    if (hasBeenDismissedThisSession) {
      return;
    }
    
    // Check if user has already made a choice
    const consent = localStorage.getItem('neura_cookie_consent');
    if (!consent) {
      // Show popup after a short delay for better UX
      const timer = setTimeout(() => {
        if (!hasBeenDismissedThisSession) {
          setIsVisible(true);
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Save consent to backend for compliance tracking
  const saveConsentToBackend = async (consentData) => {
    try {
      await fetch(`${BACKEND_URL}/api/consent/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...consentData,
          user_agent: navigator.userAgent
        })
      });
    } catch (err) {
      console.error('Failed to save consent to backend:', err);
    }
  };

  const dismissModal = useCallback(() => {
    hasBeenDismissedThisSession = true;
    setIsVisible(false);
  }, []);

  const handleAcceptAll = async () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('neura_cookie_consent', JSON.stringify(allAccepted));
    await saveConsentToBackend(allAccepted);
    dismissModal();
  };

  const handleRejectAll = async () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('neura_cookie_consent', JSON.stringify(onlyNecessary));
    await saveConsentToBackend(onlyNecessary);
    dismissModal();
  };

  const handleSavePreferences = async () => {
    const savedPreferences = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('neura_cookie_consent', JSON.stringify(savedPreferences));
    await saveConsentToBackend(savedPreferences);
    dismissModal();
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Can't toggle necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!isVisible) return null;

  // Inline styles object for guaranteed black/yellow theme
  const modalStyles = {
    container: {
      backgroundColor: '#000000',
      borderRadius: '1rem',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
      border: '1px solid rgba(234, 179, 8, 0.3)',
      overflow: 'hidden'
    },
    header: {
      backgroundColor: '#000000',
      padding: '1rem 1.25rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottom: '1px solid rgba(234, 179, 8, 0.2)'
    },
    iconBox: {
      backgroundColor: '#eab308',
      width: '40px',
      height: '40px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    content: {
      backgroundColor: '#000000',
      padding: '1.25rem'
    },
    footer: {
      backgroundColor: '#111827',
      padding: '0.75rem 1.25rem',
      borderTop: '1px solid rgba(234, 179, 8, 0.2)'
    },
    acceptBtn: {
      backgroundColor: '#eab308',
      color: '#000000',
      fontWeight: 'bold',
      padding: '0.75rem 1rem',
      borderRadius: '12px',
      border: 'none',
      cursor: 'pointer',
      flex: 1,
      fontSize: '0.875rem'
    },
    rejectBtn: {
      backgroundColor: 'transparent',
      color: '#eab308',
      fontWeight: 'bold',
      padding: '0.75rem 1rem',
      borderRadius: '12px',
      border: '2px solid rgba(234, 179, 8, 0.5)',
      cursor: 'pointer',
      flex: 1,
      fontSize: '0.875rem'
    },
    manageBtn: {
      backgroundColor: 'transparent',
      color: '#9ca3af',
      fontWeight: '500',
      padding: '0.75rem 0.5rem',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px',
      fontSize: '0.875rem'
    }
  };

  return (
    <>
      {/* Backdrop - lower z-index so login modals can appear on top */}
      <div 
        style={{ 
          position: 'fixed', 
          inset: 0, 
          backgroundColor: 'rgba(0, 0, 0, 0.6)', 
          backdropFilter: 'blur(4px)',
          zIndex: 100 
        }}
        onClick={handleRejectAll}
        aria-hidden="true"
      />
      
      {/* Cookie Consent Modal - Black/Yellow/White Theme */}
      <div 
        style={{ 
          position: 'fixed', 
          inset: 0, 
          zIndex: 101, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          padding: '1rem',
          pointerEvents: 'none'
        }}
        data-testid="cookie-consent-banner"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-consent-title"
      >
        <div style={{ width: '100%', maxWidth: '32rem', pointerEvents: 'auto' }}>
          <div style={modalStyles.container}>
            {/* Header */}
            <div style={modalStyles.header}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={modalStyles.iconBox}>
                  <Cookie style={{ width: '20px', height: '20px', color: '#000000' }} />
                </div>
                <div>
                  <h3 id="cookie-consent-title" style={{ color: '#ffffff', fontWeight: 'bold', fontSize: '1.125rem', margin: 0 }}>Cookie Preferences</h3>
                  <p style={{ color: '#9ca3af', fontSize: '0.875rem', margin: 0 }}>We value your privacy</p>
                </div>
              </div>
              <button 
                onClick={handleRejectAll}
                style={{ color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                aria-label="Close cookie preferences"
                data-testid="cookie-close-btn"
              >
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            {/* Content */}
            <div style={modalStyles.content}>
              {!showPreferences ? (
                <>
                  <p style={{ color: '#d1d5db', fontSize: '0.875rem', lineHeight: '1.625', marginBottom: '1rem' }}>
                    We use cookies to enhance your browsing experience and analyse our traffic. 
                    By clicking &ldquo;Accept All&rdquo;, you consent to our use of cookies.
                  </p>
                  
                  <div style={{ 
                    backgroundColor: 'rgba(234, 179, 8, 0.1)', 
                    border: '1px solid rgba(234, 179, 8, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '0.75rem',
                    color: '#9ca3af',
                    marginBottom: '1.25rem',
                    padding: '0.75rem',
                    borderRadius: '8px'
                  }}>
                    <Shield style={{ width: '16px', height: '16px', color: '#eab308' }} />
                    <span>GDPR & UK Data Protection compliant. Users must be 13+ years old.</span>
                  </div>

                  {/* Action Buttons - Yellow theme */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        onClick={handleAcceptAll}
                        style={modalStyles.acceptBtn}
                        data-testid="cookie-accept-all"
                      >
                        Accept All
                      </button>
                      <button
                        onClick={handleRejectAll}
                        style={modalStyles.rejectBtn}
                        data-testid="cookie-reject-all"
                      >
                        Reject
                      </button>
                    </div>
                    <button
                      onClick={() => setShowPreferences(true)}
                      style={modalStyles.manageBtn}
                      data-testid="cookie-manage-preferences"
                    >
                      <Settings style={{ width: '16px', height: '16px' }} />
                      Manage
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Preferences Panel - Black/Yellow Theme */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '1rem', maxHeight: '240px', overflowY: 'auto' }}>
                    {/* Necessary Cookies */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', backgroundColor: '#111827', borderRadius: '12px', border: '1px solid rgba(234, 179, 8, 0.2)' }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ fontWeight: '600', color: '#ffffff', fontSize: '0.875rem', margin: 0 }}>Essential</h4>
                        <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0 }}>Required for website to function</p>
                      </div>
                      <div style={{ width: '40px', height: '20px', backgroundColor: '#eab308', borderRadius: '10px', position: 'relative', cursor: 'not-allowed' }}>
                        <div style={{ position: 'absolute', right: '2px', top: '2px', width: '16px', height: '16px', backgroundColor: '#000000', borderRadius: '50%', boxShadow: '0 1px 2px rgba(0,0,0,0.3)' }} />
                      </div>
                    </div>

                    {/* Analytics Cookies */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', backgroundColor: '#111827', borderRadius: '12px', border: '1px solid rgba(234, 179, 8, 0.2)' }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ fontWeight: '600', color: '#ffffff', fontSize: '0.875rem', margin: 0 }}>Analytics</h4>
                        <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0 }}>Helps us understand visitor behavior</p>
                      </div>
                      <button
                        onClick={() => togglePreference('analytics')}
                        style={{ width: '40px', height: '20px', backgroundColor: preferences.analytics ? '#eab308' : '#4b5563', borderRadius: '10px', position: 'relative', border: 'none', cursor: 'pointer', transition: 'background-color 0.2s' }}
                        data-testid="cookie-toggle-analytics"
                      >
                        <div style={{ position: 'absolute', top: '2px', width: '16px', height: '16px', backgroundColor: '#000000', borderRadius: '50%', boxShadow: '0 1px 2px rgba(0,0,0,0.3)', transition: 'left 0.2s, right 0.2s', ...(preferences.analytics ? { right: '2px' } : { left: '2px' }) }} />
                      </button>
                    </div>

                    {/* Marketing Cookies */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', backgroundColor: '#111827', borderRadius: '12px', border: '1px solid rgba(234, 179, 8, 0.2)' }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ fontWeight: '600', color: '#ffffff', fontSize: '0.875rem', margin: 0 }}>Marketing</h4>
                        <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0 }}>Personalised advertisements</p>
                      </div>
                      <button
                        onClick={() => togglePreference('marketing')}
                        style={{ width: '40px', height: '20px', backgroundColor: preferences.marketing ? '#eab308' : '#4b5563', borderRadius: '10px', position: 'relative', border: 'none', cursor: 'pointer', transition: 'background-color 0.2s' }}
                        data-testid="cookie-toggle-marketing"
                      >
                        <div style={{ position: 'absolute', top: '2px', width: '16px', height: '16px', backgroundColor: '#000000', borderRadius: '50%', boxShadow: '0 1px 2px rgba(0,0,0,0.3)', transition: 'left 0.2s, right 0.2s', ...(preferences.marketing ? { right: '2px' } : { left: '2px' }) }} />
                      </button>
                    </div>

                    {/* Functional Cookies */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', backgroundColor: '#111827', borderRadius: '12px', border: '1px solid rgba(234, 179, 8, 0.2)' }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ fontWeight: '600', color: '#ffffff', fontSize: '0.875rem', margin: 0 }}>Functional</h4>
                        <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0 }}>Enhanced features</p>
                      </div>
                      <button
                        onClick={() => togglePreference('functional')}
                        style={{ width: '40px', height: '20px', backgroundColor: preferences.functional ? '#eab308' : '#4b5563', borderRadius: '10px', position: 'relative', border: 'none', cursor: 'pointer', transition: 'background-color 0.2s' }}
                        data-testid="cookie-toggle-functional"
                      >
                        <div style={{ position: 'absolute', top: '2px', width: '16px', height: '16px', backgroundColor: '#000000', borderRadius: '50%', boxShadow: '0 1px 2px rgba(0,0,0,0.3)', transition: 'left 0.2s, right 0.2s', ...(preferences.functional ? { right: '2px' } : { left: '2px' }) }} />
                      </button>
                    </div>
                  </div>

                  {/* Save Preferences Button */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setShowPreferences(false)}
                      style={{ flex: 1, backgroundColor: 'transparent', border: '2px solid #4b5563', color: '#9ca3af', fontWeight: '500', padding: '0.625rem 1rem', borderRadius: '12px', cursor: 'pointer', fontSize: '0.875rem' }}
                    >
                      Back
                    </button>
                    <button
                      onClick={handleSavePreferences}
                      style={{ flex: 1, backgroundColor: '#eab308', color: '#000000', fontWeight: 'bold', padding: '0.625rem 1rem', borderRadius: '12px', border: 'none', cursor: 'pointer', fontSize: '0.875rem' }}
                      data-testid="cookie-save-preferences"
                    >
                      Save
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div style={modalStyles.footer}>
              <p style={{ fontSize: '0.75rem', color: '#9ca3af', textAlign: 'center', margin: 0 }}>
                You must be 13 years or older to use this site. See our{' '}
                <a href="/privacy-policy" style={{ color: '#eab308', textDecoration: 'none' }}>Privacy Policy</a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
